import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { Passbook } from '@/shared/types';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { useChannelQuotas } from '../model/use-channel-quotas';
import type { QuotaChannel } from '../model/ledger';

import { RechargeDialog } from './recharge-dialog';
import {
  formatCount,
  quotaLimit,
  quotaSignal,
  remainingPercent,
  type AccountCapacity,
  type QuotaSignal,
} from '@/features/marketing-shared';

function meterColor(signal: QuotaSignal): string {
  return signal === 'approaching' ? 'bg-warning' : 'bg-primary';
}

function ringColor(signal: QuotaSignal): string {
  return signal === 'approaching' ? 'hsl(var(--warning))' : 'hsl(var(--primary))';
}

function balanceTitle(label: string, quota: AccountCapacity): string {
  const name = label.toLowerCase();
  if (quota.available <= 0) return `Sem saldo de ${name}`;
  return `Saldo de ${name}: ${formatCount(quota.available)} de ${formatCount(quotaLimit(quota))}`;
}

function RemainingMeter({
  label,
  quota,
  collapsed,
  onOpen,
}: {
  label: string;
  quota: AccountCapacity;
  collapsed?: boolean;
  onOpen: () => void;
}) {
  const left = remainingPercent(quota);
  const signal = quotaSignal(quota);
  const empty = quota.available <= 0;
  const title = balanceTitle(label, quota);

  if (collapsed) {
    return (
      <button
        type="button"
        title={title}
        aria-label={title}
        className="relative grid size-10 place-items-center"
        onClick={onOpen}
      >
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background: empty
              ? 'hsl(var(--muted))'
              : `conic-gradient(${ringColor(signal)} ${left * 3.6}deg, hsl(var(--muted)) 0deg)`,
          }}
        />
        <span className="absolute inset-[3px] rounded-full bg-background" />
        <span className={cn('relative text-[10px] font-semibold tabular-nums', empty ? 'text-muted-foreground' : 'text-foreground')}>
          {empty ? '0' : left}
        </span>
      </button>
    );
  }

  return (
    <div className="space-y-1.5" title={title}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium">{label}</span>
        {empty ? (
          <span className="text-xs text-muted-foreground">Sem saldo</span>
        ) : (
          <span className={cn('text-xs font-semibold tabular-nums', signal === 'approaching' ? 'text-warning' : 'text-foreground')}>
            {formatCount(quota.available)}
          </span>
        )}
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        {empty ? null : (
          <div
            className={cn('h-full rounded-full transition-[width] duration-500', meterColor(signal))}
            style={{ width: `${left}%` }}
          />
        )}
      </div>
    </div>
  );
}

function MeterSkeleton({ collapsed }: { collapsed?: boolean }) {
  if (collapsed) return <Skeleton className="size-10 rounded-full" />;
  return (
    <div className="space-y-1.5" aria-hidden>
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-12 rounded" />
        <Skeleton className="h-3 w-10 rounded" />
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
    </div>
  );
}

export function SidebarQuota({
  passbook,
  passbookId,
  collapsed,
}: {
  passbook?: Passbook | null;
  passbookId?: string;
  collapsed?: boolean;
}) {
  const emailOn = passbook?.hasEmailMarketing === true;
  const smsOn = passbook?.hasSmsMarketing === true;
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<QuotaChannel>('email');
  // Só o que veio do servidor: o saldo-semente da demonstração fazia a barra nascer cheia e esvaziar.
  const { data, isPending } = useChannelQuotas(emailOn || smsOn ? passbookId : undefined);

  if (!passbookId || (!emailOn && !smsOn)) return null;

  const enabled = [
    emailOn ? { channel: 'email' as const, label: 'E-mail' } : null,
    smsOn ? { channel: 'sms' as const, label: 'SMS' } : null,
  ].filter((row) => row != null);
  const channels = enabled.map((row) => row.channel);

  const openRecharge = (next: QuotaChannel) => {
    setChannel(next);
    setOpen(true);
  };

  return (
    <div className={cn('mb-3', collapsed ? 'flex flex-col items-center gap-2' : 'space-y-3')}>
      {collapsed ? null : (
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Saldo</p>
      )}
      {isPending
        ? enabled.map((row) => <MeterSkeleton key={row.channel} collapsed={collapsed} />)
        : data
          ? enabled.map((row) => (
              <RemainingMeter
                key={row.channel}
                label={row.label}
                quota={data[row.channel]}
                collapsed={collapsed}
                onOpen={() => openRecharge(row.channel)}
              />
            ))
          : collapsed
            ? null
            : <p className="text-xs text-muted-foreground">Saldo indisponível agora.</p>}
      {collapsed ? (
        <Button
          type="button"
          variant="secondary"
          size="icon-sm"
          title="Recarregar"
          aria-label="Recarregar"
          onClick={() => openRecharge(channels[0])}
        >
          <Plus className="text-primary" />
        </Button>
      ) : (
        <Button type="button" variant="secondary" size="sm" className="w-full" onClick={() => openRecharge(channels[0])}>
          <Plus className="text-primary" />
          Recarregar
        </Button>
      )}
      <RechargeDialog open={open} onOpenChange={setOpen} passbookId={passbookId} channel={channel} channels={channels} />
    </div>
  );
}
